import { type JSX, useState } from 'react';

import { UsersApi } from 'Api/Users/UsersApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_PROHIBITED } from 'Config/Icons';
import { UserNameFormat, getUserNameFromUser } from 'Helpers/UserUtils';
import { UserResponse } from 'Models/User';

import styles from './DeactivateUserModal.module.css';

export interface DeactivateUserModalProps {
    hideModal: () => void;
    userSelectedForDeactivation: UserResponse;
    usersApi: UsersApi;
    userDeactivated: () => void;
}

export const DeactivateUserModal = (props: DeactivateUserModalProps): JSX.Element => {
    const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
    const [failureMessage, setFailureMessage] = useState<string | undefined>(undefined);
    const [isDeactivating, setIsDeactivating] = useState<boolean>(false);

    const deactivateUser = async (): Promise<void> => {
        setIsDeactivating(true);
        setSuccessMessage(undefined);
        setFailureMessage(undefined);
        try {
            await props.usersApi.deactivateUser(props.userSelectedForDeactivation.cognito_subject);
            setSuccessMessage('User deactivated.');
            props.userDeactivated();
        } catch (error) {
            handleRequestError(error);
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleRequestError = (error: Error): void => {
        setFailureMessage(error.message);
        setSuccessMessage(undefined);
    };

    const hideModal = (): void => {
        if (!isDeactivating) {
            props.hideModal();
        }
    };

    return (
        <Modal open header="Deactivate">
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}
            <div className={styles.deactivateText}>Deactivating a user will prevent the user from logging in or receiving notifications.</div>
            <div className={styles.deactivateText}>Are you sure you want to deactivate: {getUserNameFromUser(props.userSelectedForDeactivation, UserNameFormat.FIRST_SPACE_LAST)}?</div>

            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={hideModal} disabled={isDeactivating} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
                <Button variant="danger" onClick={() => deactivateUser()} loadingText={'Deactivating...'} isLoading={isDeactivating} disabled={successMessage ? true : false} fontAwesomeImage={ICON_PROHIBITED}>
                    DEACTIVATE
                </Button>
            </div>
        </Modal>
    );
};
